import { useEffect, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  TextField,
  Table,
  TableMetadata,
  Typography,
  useTable,
  AutocompleteMultipleSelect,
  Label,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, ExportTypeEnum } from "@king-ict/eupisi-ui/models";
import {
  Row,
  Col,
  Container,
  Modal as BSModal,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DatePickerRange, CommitteeDateRangePicker } from "./components";
import { DEFAULT_COUNTY_COMMITTEE } from "./constants";
import {
  getCountyCommitteeById,
  createCountyCommitteeById,
  searchCountyCommitteeEmployee,
  saveCountyCommittee,
  getCountyCommitteeEmployeeOib,
  getCountyCommitteeInstitutionListExport,
} from "../../api/Committee/CountyCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuthCounties } from "../../hooks/auth";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import { ValidationFailure } from "../../models";
import {
  CountyCommittee,
  CommitteeEmployee,
  OibEmployee,
} from "../../models/Committee";
import { CodeBookPagedFilter, SaveResult } from "../../models/Shared";
import { useGetInstitutionListForUserQuery } from "../../queries/codeBookQuery";
import { countyCommitteeDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";
import { useDeleteCommittee } from "./hooks/useDeleteCommittee";
import {
  canEmployeeBeRemoved,
  getCommitteeEmployeeDisplayName,
  isEmployeeActive,
  updateEmployeesWithPresident,
} from "./utils";
import {
  setupCommitteeEmployees,
  updateCommitteeEmployee,
} from "./utils/committee.util";
import { OIB_REGEX } from "../../constants";

const DEFAULT_TAB = "members";

export function CountyCommitteeEdit() {
  const { id, countyId } = useParams<{ id: string; countyId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const counties = useAuthCounties();

  const [item, setItem] = useState<CountyCommittee>(DEFAULT_COUNTY_COMMITTEE);
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);

  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-5",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "edit",
        header: t("common.edit"),
        accessor: (employee) => {
          return (
            <div className={"text-right"}>
              <Button
                size="sm"
                outline={true}
                onClick={() => handleDetailsClicked(employee)}
                disabled={!isEditing}
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "expertCommissionPosition",
        header: t("common.expertCommission.position"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeePosition?.name}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          const isActive = isEmployeeActive(emp);

          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.rowKeyId!}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => handlePresidentChange(emp.rowKeyId!)}
                value={emp.rowKeyId!}
                disabled={!emp.employee || !isEditing || !isActive}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [filteredEmployees, setFilteredEmployees] = useState<CodeBook[]>([]);
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const [employee, setEmployee] = useState<CodeBook>();
  const [oibEmployee, setOibEmployee] = useState<OibEmployee | null>(null);
  const [editedActiveTo, setEditedActiveTo] = useState<Date | null>(null);
  const [showOibAlert, setShowOibAlert] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tabKey, setTabKey] = useState(DEFAULT_TAB);
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const [exportFormat, setExportFormat] = useState<ExportTypeEnum>(
    ExportTypeEnum.Excel
  );

  const { handleDeleteCommittee, isDeleteEnabled } = useDeleteCommittee({
    isEnabled: !item.hasDecisions,
    committeeType: "county",
    committeeId: +id,
  });

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        const result = await getCountyCommitteeById(+id);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
      if (!isNaN(+countyId) && +countyId > 0) {
        const result = await createCountyCommitteeById(+countyId);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, countyId]);

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
    setEmployee(expertCommissionEmployee.employee ?? undefined);
    setOibEmployee(expertCommissionEmployee.oibEmployee);
    setEditedActiveTo(
      expertCommissionEmployee.deactivationDate
        ? new Date(expertCommissionEmployee.deactivationDate)
        : null
    );
  };
  const handleDateChange = (date: Date, field: "dateFrom" | "dateTo") => {
    const newItem = {
      ...item,
      [field]: FormatUtils.setDateToUTCMidnight(date),
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleEmployeeFilterChange = async (filter: string) => {
    const search: CodeBookPagedFilter = {
      pageSize: 100,
      page: 1,
      search: filter,
    };
    const result = await searchCountyCommitteeEmployee(search);
    setFilteredEmployees(result.results);
  };
  const handleEmployeeChangeMultiselect = (e: any) => {
    if (!canEmployeeBeRemoved(editedActiveTo, editing)) {
      return;
    }

    const employeeList = e.target.value as CodeBook[];
    const employeeCodeBook =
      employeeList.length > 0 ? employeeList[0] : undefined;
    setEmployee(employeeCodeBook);
    if (employeeCodeBook) {
      setOibEmployee(null);
    }
  };
  const handlePresidentChange = (rowKeyId: CommitteeEmployee["rowKeyId"]) => {
    const updatedEmployees = updateEmployeesWithPresident(tableData, rowKeyId);

    setTableData(updatedEmployees);
    setItem({
      ...item,
      employees: updatedEmployees,
    });
    dispatch(setIsEdited());
  };
  const handleNoteChange = (e: any) => {
    const note = e.target.value;
    const newItem: CountyCommittee = {
      ...item,
      note: note,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleOibChanged = (e: any) => {
    const oib = e.target.value as string;
    const newOibEmployee: OibEmployee = {
      ...oibEmployee,
      oib: oib,
      lastName: undefined,
    };
    setOibEmployee(newOibEmployee);
    setShowOibAlert(null);
  };
  const handleOibSystemClick = async () => {
    if (oibEmployee?.oib) {
      const result = await getCountyCommitteeEmployeeOib(oibEmployee.oib);
      if (result && result.item && result.item.oib) {
        const newOibEmployee: OibEmployee = {
          ...oibEmployee,
          firstName: result.item.firstName,
          lastName: result.item.lastName,
        };
        setOibEmployee(newOibEmployee);
        setEmployee(undefined);
      } else {
        setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
      }
    }
  };

  const handleDownload = async () => {
    if (!item.countyId) return;

    await getCountyCommitteeInstitutionListExport(item.countyId, exportFormat);
  };
  const onModalClose = () => {
    setEditedActiveTo(null);
    setEditing(null);
  };

  const deleteButtonVisibility = isEditing
    ? isDeleteEnabled
      ? "show"
      : "disable"
    : "hide";

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditing ? "hide" : "show"}
        pageTitle={t("common.expertCommission.countyPageNameEdit")}
        onClickEdit={() => {
          setIsEditing(true);
          dispatch(setIsEdited());
        }}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.county")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {counties.find((c) => c.id === item.countyId)?.name}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <CommitteeDateRangePicker
            dateFrom={item.dateFrom ? new Date(item.dateFrom) : null}
            dateTo={item.dateTo ? new Date(item.dateTo) : null}
            onDateFromChange={(date) => handleDateChange(date, "dateFrom")}
            onDateToChange={(date) => handleDateChange(date, "dateTo")}
            errors={errors}
            disabled={!isEditing}
            plaintext={!isEditing}
            colMd={2}
          />
          <Col md={4} />
        </Row>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={handleNoteChange}
              errorText={
                errors?.find((e) => e.propertyName === "Note")?.message
              }
              maxLength={5000}
              disabled={!isEditing}
            />
          </Col>
        </Row>
      </FormCard>
      <Tabs
        id="controlled-tab-example"
        activeKey={tabKey ?? "basicInfo"}
        onSelect={(k) => setTabKey(k ?? DEFAULT_TAB)}
      >
        <Tab
          title={t("common.expertCommission.tabs.memberList")}
          eventKey={DEFAULT_TAB}
          className={
            errors?.some((e) => e.propertyName?.startsWith("Employees"))
              ? "text-danger"
              : undefined
          }
        >
          <FormCard>
            <Table {...tableProps} />
          </FormCard>
        </Tab>
        <Tab
          title={t("common.expertCommission.tabs.institutionList")}
          eventKey="institutions"
          disabled={+id === 0}
          className={
            errors?.some((e) => e.propertyName?.startsWith("InstitutionList"))
              ? "text-danger"
              : undefined
          }
        >
          <ul>
            {institutions &&
              institutions
                ?.filter(
                  (v) => v.countyId === item.countyId && !v.hasRegionalCommittee
                )
                .map((v) => (
                  <li>
                    <Typography component="span">{v.name}</Typography>
                  </li>
                ))}
          </ul>
        </Tab>
      </Tabs>

      <ToolbarEdit
        isEdited={isEditing}
        showCancel={"show"}
        showSave={isEditing ? "show" : "hide"}
        showDownload={"show"}
        saveText={t("common.saveAndApprove")}
        handleSave={async () => saveCountyCommittee(item)}
        redirectUrlSaveOK={countyCommitteeDetailUrl}
        setSaveErrors={setErrors}
        onClickDownload={handleDownload}
        onClickChangeFormat={() =>
          exportFormat === ExportTypeEnum.Excel
            ? setExportFormat(ExportTypeEnum.Csv)
            : setExportFormat(ExportTypeEnum.Excel)
        }
        onSaveSuccess={(data: SaveResult<CountyCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
        showDelete={deleteButtonVisibility}
        onClickDelete={handleDeleteCommittee}
      />
      {/* CommitteeEmployee Modal */}
      <BSModal
        size="lg"
        backdrop="static"
        centered
        animation={false}
        show={editing != null}
        onClose={onModalClose}
        onHide={onModalClose}
      >
        <BSModal.Header>{editing?.committeeRole!.name}</BSModal.Header>
        <BSModal.Body>
          <Container>
            {showOibAlert === null || (
              <InfoPanel
                color="danger"
                message=""
                dismissible={false}
                fullWidth={true}
              >
                {showOibAlert}
              </InfoPanel>
            )}
            <Row>
              <Col md={9}>
                <AutocompleteMultipleSelect
                  label={t("common.expertCommission.employee")}
                  name={"employeeId"}
                  value={employee?.id !== undefined ? [employee] : []}
                  valueProp={"id"}
                  textProp={"name"}
                  onChange={handleEmployeeChangeMultiselect}
                  selectOptions={filteredEmployees}
                  onFilterChange={handleEmployeeFilterChange}
                  maximumSelectedItems={1}
                />
              </Col>
            </Row>
            {editing?.committeeRole.checkOIB && (
              <>
                <Row>
                  <Col md={8}>
                    <TextField
                      label={t("common.schoolCandidateModel.oib")}
                      value={oibEmployee?.oib}
                      onChange={handleOibChanged}
                      disabled={employee?.id !== undefined}
                    />
                  </Col>
                  <Col md={4}>
                    <br />
                    <Button
                      onClick={handleOibSystemClick}
                      disabled={
                        !(
                          oibEmployee?.oib !== undefined &&
                          OIB_REGEX.test(oibEmployee.oib)
                        )
                      }
                      size="sm"
                    >
                      {t("common.schoolCandidateModel.connectOibSystem")}
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div>
                      {oibEmployee?.lastName !== undefined
                        ? `${oibEmployee?.oib}, ${oibEmployee?.lastName}, ${oibEmployee?.firstName}`
                        : ""}
                    </div>
                  </Col>
                </Row>
              </>
            )}
            <DatePickerRange
              onChange={setEditedActiveTo}
              value={editedActiveTo}
              disabled={!employee || editing?.id === 0}
            />
          </Container>
        </BSModal.Body>
        <BSModal.Footer>
          <Button
            onClick={() => {
              setEmployee(undefined);
              setOibEmployee(null);
              setEditing(null);
              setFilteredEmployees([]);
              setShowOibAlert(null);
              setEditedActiveTo(null);
            }}
            size="sm"
            outline={true}
          >
            {t("common.quit")}
          </Button>
          <Button
            onClick={() => {
              editing!.employee =
                employee && employee.id ? employee : undefined;
              editing!.oibEmployee =
                oibEmployee && oibEmployee.lastName ? oibEmployee : null;
              editing!.deactivationDate =
                FormatUtils.formatToISOString(editedActiveTo);

              if (editing!.committeeRole.id === 20) {
                let presidentExist = false;
                tableData.forEach(
                  (el) => (presidentExist = el.isPresident || presidentExist)
                );
                editing!.isPresident = !presidentExist;
              }

              setFilteredEmployees([]);
              setEmployee(undefined);
              setOibEmployee(null);
              setEditedActiveTo(null);
              dispatch(setIsEdited());
              setEditing(null);
              setShowOibAlert(null);
              setItem((currItem) =>
                updateCommitteeEmployee(currItem, editing!)
              );
            }}
            size="sm"
          >
            {t("common.save")}
          </Button>
        </BSModal.Footer>
      </BSModal>
    </ContentArea>
  );
}
